import React from "react";
import Avatar, { AvatarProps } from "@material-ui/core/Avatar";
import { useApiCache } from "customHooks/useApi";
import { useAuthState } from "_ReactContext/AuthContext";
import { getFirstLetterNameLastName } from "functions/util";
import { useBatchAvatar } from "customHooks/useBatchAvatars";

export type avatarCompleoType = "user" | "applicant" | "applicantExternal";

interface IProps {
    type: avatarCompleoType;
    idParams: Compleo.IObject;
}

// Component for applicant avatars using batch fetching
const ApplicantAvatar = (props: IProps & AvatarProps) => {
    const { idParams, ...other } = props;
    const applicantId = idParams?.id;
    const { avatar, isAvailable } = useBatchAvatar(applicantId);

    // If batch context not available, fall back to individual fetch
    if (!isAvailable) {
        return <CompleoAvatarIndividual {...props} />;
    }

    if (avatar?.photo) {
        return (
            <Avatar
                alt={avatar?.name}
                {...other}
                src={`data:image/jpeg;base64,${avatar.photo}`}
            />
        );
    } else if (avatar?.name) {
        return (
            <Avatar {...other} src="" alt={avatar?.name}>
                {getFirstLetterNameLastName(avatar?.name || "")}
            </Avatar>
        );
    } else {
        return <Avatar {...other} alt="" src="" />;
    }
};

// Original component for individual fetching (user, applicantExternal, or fallback)
const CompleoAvatarIndividual = (props: IProps & AvatarProps) => {
    const { idParams, type, ...other } = props;
    const { company } = useAuthState();
    const { params, path } = getQueryParams(type, idParams, company.companyId);
    const [getAvatarReturn, executeQueryAvatarReturn] = useApiCache(
        path,
        "post",
        params,
        true,
        1000 * 60 * 10
    );
    const data = getAvatarReturn?.response?.data;

    React.useEffect(() => {
        executeQueryAvatarReturn(params);
    }, [idParams?.id, idParams?.email]);

    if (data?.photo) {
        return (
            <Avatar
                alt={data?.name}
                {...other}
                src={`data:image/jpeg;base64,${data.photo}`}
            />
        );
    } else if (data?.name) {
        return (
            <Avatar {...other} src="" alt={data?.name}>
                {getFirstLetterNameLastName(data?.name || "")}
            </Avatar>
        );
    } else {
        return <Avatar {...other} alt="" src="" />;
    }
};

const CompleoAvatar = (props: IProps & AvatarProps) => {
    const { type } = props;

    // Use batch fetching for applicant type
    if (type === "applicant") {
        return <ApplicantAvatar {...props} />;
    }

    // Use individual fetching for user and applicantExternal
    return <CompleoAvatarIndividual {...props} />;
};

const getQueryParams = (
    type: avatarCompleoType,
    idParams: Compleo.IObject,
    companyId: number
) => {
    switch (type) {
        case "applicant":
            return {
                params: {
                    id: idParams?.id,
                    companyId: companyId
                },
                path: `/applicant/getavatarapplicant`
            };
        case "applicantExternal":
            return {
                params: {
                    id: idParams?.id,
                    evaluationId: idParams?.evaluationId
                },
                path: `/applicant/getavatarapplicantexternal`
            };
        case "user":
            return {
                params: {
                    email: idParams?.email,
                    companyId: companyId
                },
                path: `/user/getavataruser`
            };
    }
};

export default CompleoAvatar;
